import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { API_ENDPOINTS } from '../constants/api';
import { toProperCase } from '../utils/textUtils';
import './EditFieldModal.css';

interface Field {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number;
  geojson: string;
  state: string;
  county: string;
  climate_zone: string;
  is_current?: boolean;
  created_at: string;
  updated_at: string;
}

interface EditFieldModalProps {
  field: Field | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedField: Field) => void;
  onDelete?: (fieldId: string) => void;
  userToken: string;
  selectedOrganizationId: string;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
  field,
  isOpen,
  onClose,
  onSave,
  onDelete,
  userToken,
  selectedOrganizationId
}) => {
  const [fieldName, setFieldName] = useState('');
  const [acres, setAcres] = useState<number>(0);
  const [isCurrent, setIsCurrent] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (field) {
      console.log('🎯 Setting field data:', field);
      console.log('🎯 Field acres:', field.acres, typeof field.acres);
      setFieldName(field.fieldname);
      setAcres(typeof field.acres === 'string' ? parseFloat(field.acres) || 0 : field.acres);
      setIsCurrent(field.is_current !== undefined ? field.is_current : true);
    }
  }, [field]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!field || !fieldName.trim()) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.FIELDS}/${field.field_id}?organization_id=${selectedOrganizationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          fieldname: toProperCase(fieldName.trim()),
          acres: parseFloat(acres.toString()),
          is_current: isCurrent
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update field');
      }

      const updatedField = await response.json();
      onSave(updatedField);
      onClose();
    } catch (err) {
      console.error('Error updating field:', err);
      setError(err instanceof Error ? err.message : 'Failed to update field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!field || !onDelete) return;
    
    setIsDeleting(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.FIELDS}/${field.field_id}?organization_id=${selectedOrganizationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete field');
      }

      onDelete(field.field_id);
      onClose();
    } catch (err) {
      console.error('Error deleting field:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete field');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'fieldName') {
      const fieldName = toProperCase(value);
      setFieldName(fieldName);
    } else if (name === 'acres') {
      const numValue = parseFloat(value) || 0;
      setAcres(numValue);
    } else if (type === 'checkbox' && name === 'is_current') {
      setIsCurrent(checked);
    }
  };

  if (!isOpen || !field) {
    return null;
  }

  // If showing delete confirmation, only show that modal
  if (showDeleteConfirm) {
    return (
      <div className="modal-overlay">
        <div className="modal-content delete-confirm-modal">
          <div className="modal-header">
            <h2>Confirm Delete</h2>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this field?</p>
            <p className="warning-text">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
          </div>
          <div className="modal-actions">
            <button 
              type="button" 
              onClick={handleDeleteCancel}
              className="btn-secondary"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleDeleteConfirm}
              className="btn-delete"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show the edit modal
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Edit Field</h2>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-group">
            <label htmlFor="fieldName">Field Name *</label>
            <input
              type="text"
              id="fieldName"
              name="fieldName"
              value={fieldName}
              onChange={handleInputChange}
              required
              placeholder="Enter field name"
              autoFocus
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="acres">Acres *</label>
            <input
              type="number"
              id="acres"
              name="acres"
              value={acres}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
              placeholder="Enter acres"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_current"
                checked={isCurrent}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">Currently Active</span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleDeleteClick}
              className="btn-delete"
              disabled={isSaving || isDeleting}
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSaving || isDeleting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving || isDeleting || !fieldName.trim()}
            >
              {isSaving ? 'Updating...' : 'Update Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFieldModal;
